import type { ReactNode } from "react";
import { ClaimHelper } from "../utils/helpers";
import { roles } from "./claimSettings";
import { GridCommunityDto, UserCommunityDto } from "../../services/dto";

export enum ConfigurationSectionType {
    None = 0,
    System = 1,
    Account = 2,
    Admin = 3,
    Questionnaire = 4,
    PopHub = 5,
}

export type IConfigurationItem = {
    title: string;
    subtitle: string | ReactNode;
    href: string;
    isDisplayed?: boolean;
};

export type IConfigurationSection = {
    title: string;
    subtitle?: string | ReactNode;
    items: IConfigurationItem[];
    isDisplayed?: boolean;
    sectionType?: ConfigurationSectionType;
};

interface IConfigurationProps {
    userCommunities: UserCommunityDto[] | undefined;
}

const technicalFeedSubtitle = (manageLabel: string): ReactNode => (
    <>
        <span className="d-block mb-1">{manageLabel}</span>
        <span
            className="d-flex align-items-start"
            style={{ color: "var(--color-warning-dark)" }}
        >
            <i
                className="bi bi-exclamation-triangle-fill me-1"
                style={{ fontSize: 12, marginTop: 3, flexShrink: 0 }}
            />
            <span>
                This is a technical setup. Please use caution when making updates,
                as changes may cause malfunctions in <b>News and Events</b>.
            </span>
        </span>
    </>
);

export function GetConfigurationSections(
    props: IConfigurationProps,
): IConfigurationSection[] {
    const communityTitle = props.userCommunities?.length == 1 ? "Community" : "Communities";
    const communityHref = props.userCommunities?.length == 1 ? `/community/${props.userCommunities[0].communityId}` : "/community";
    const result: IConfigurationSection[] = [
        {
            title: "System settings",
            isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
            sectionType: ConfigurationSectionType.System,
            items: [
                {
                    title: "Lookup values",
                    subtitle: "Manage lookup values",
                    href: "/system/lookupvalue",
                    isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
                },
                {
                    title: "Lookup types",
                    subtitle: "Manage lookup types",
                    href: "/system/lookuptype",
                    isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
                },
                {
                    title: "Lookup groups",
                    subtitle: "Manage lookup groups",
                    href: "/system/lookupgroup",
                    isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
                },
                {
                    title: "Settings",
                    subtitle: "Manage settings",
                    href: "/system/settings",
                    isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
                },
                {
                    title: "Counties",
                    subtitle: "Manage counties/parishes",
                    href: "/system/county",
                    isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
                },
                {
                    title: "States",
                    subtitle: "Manage states",
                    href: "/system/states",
                    isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
                },
            ],
        },
        {
            title: "Account settings",
            isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
            sectionType: ConfigurationSectionType.Account,
            items: [
                {
                    title: "Lookup values",
                    subtitle: "Manage lookup values",
                    href: "/account/lookupvalue",
                    isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
                },
                {
                    title: "Lookup types",
                    subtitle: "Manage lookup types",
                    href: "/account/lookuptype",
                    isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
                },
                {
                    title: "Lookup groups",
                    subtitle: "Manage lookup groups",
                    href: "/account/lookupgroup",
                    isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
                },
                {
                    title: "Claims",
                    subtitle: "Manage claims",
                    href: "/account/claim",
                    isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
                },
                {
                    title: "Roles",
                    subtitle: "Manage roles",
                    href: "/account/roles",
                    isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
                },
                {
                    title: "Settings",
                    subtitle: "Manage settings",
                    href: "/account/settings",
                    isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
                },
                {
                    title: "Users",
                    subtitle: "Manage users",
                    href: "/account/user",
                    isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
                },
            ],
        },
        {
            title: "Settings",
            isDisplayed: ClaimHelper.hasRole([
                roles.SystemAdmin,
                roles.CommunityAdmin,
            ]),
            sectionType: ConfigurationSectionType.Admin,
            items: [
                {
                    title: communityTitle,
                    subtitle: `Manage ${communityTitle}`,
                    href: communityHref,
                    isDisplayed: ClaimHelper.hasRole([
                        roles.CommunityAdmin,
                        roles.SystemAdmin,
                    ]),
                },
                {
                    title: "Neighborhoods",
                    subtitle: "Manage community neighborhoods",
                    href: "/neighborhoods",
                    isDisplayed: ClaimHelper.hasRole([
                        roles.CommunityAdmin,
                        roles.SystemAdmin,
                    ]),
                },
                {
                    title: "Users",
                    subtitle: "Manage users",
                    href: "/account/user",
                    isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
                },
                {
                    title: "Community resource categories",
                    subtitle: "Manage community resource categories",
                    href: "/community/communityresourcecategories",
                    isDisplayed: ClaimHelper.hasRole([
                        roles.CommunityAdmin,
                        roles.SystemAdmin,
                    ]),
                },
                {
                    title: "Nonprofit categories",
                    subtitle: "Manage nonprofit categories",
                    href: "/herenow/nonprofit",
                    isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
                },
                //{
                //  title: "Lookup values",
                //  subtitle: "Manage lookup values",
                //  href: "/admin/lookupvalue",
                //  isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
                //},
                {
                    title: "News RSS feeds",
                    subtitle: technicalFeedSubtitle("Manage news RSS feeds"),
                    href: "/herenow/newsfeeds",
                    isDisplayed: ClaimHelper.hasRole([
                        roles.SystemAdmin,
                        roles.CommunityAdmin,
                    ]),
                },
                {
                    title: "Event RSS feeds",
                    subtitle: technicalFeedSubtitle("Manage event RSS feeds"),
                    href: "/herenow/events",
                    isDisplayed: ClaimHelper.hasRole([
                        roles.SystemAdmin,
                        roles.CommunityAdmin,
                    ]),
                },
                //{
                //  title: "Lookup types",
                //  subtitle: "Manage lookup types",
                //  href: "/admin/lookuptype",
                //  isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
                //},
                //{
                //  title: "Lookup groups",
                //  subtitle: "Manage lookup groups",
                //  href: "/admin/lookupgroup",
                //  isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
                //},
                //{
                //  title: "Settings",
                //  subtitle: "Manage settings",
                //  href: "/admin/settings",
                //  isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
                //},
                /*{
                  title: "Plans",
                  subtitle: "Manage plans",
                  href: "/admin/plan",
                  isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
                },
                {
                  title: "Subscriptions",
                  subtitle: "Manage subscriptions",
                  href: "/admin/subscription",
                  isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
                },*/
            ],
        },
        {
            title: "Questionnaire settings",
            isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
            sectionType: ConfigurationSectionType.Questionnaire,
            items: [
                {
                    title: "Lookup values",
                    subtitle: "Manage lookup values",
                    href: "/questionnaire/lookupvalue",
                    isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
                },
                {
                    title: "Lookup types",
                    subtitle: "Manage lookup types",
                    href: "/questionnaire/lookuptype",
                    isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
                },
                {
                    title: "Lookup groups",
                    subtitle: "Manage lookup groups",
                    href: "/questionnaire/lookupgroup",
                    isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
                },
                {
                    title: "Settings",
                    subtitle: "Manage settings",
                    href: "/questionnaire/settings",
                    isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
                },
                {
                    title: "Question types",
                    subtitle: "Manage question types",
                    href: "/questionnaire/questiontype",
                    isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
                },
                {
                    title: "Answer types",
                    subtitle: "Manage answer types",
                    href: "/questionnaire/answertype",
                    isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
                },
            ],
        },
        {
            title: "Here&Now settings",
            isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
            sectionType: ConfigurationSectionType.PopHub,
            items: [
                {
                    title: "Lookup values",
                    subtitle: "Manage lookup values",
                    href: "/herenow/lookupvalue",
                    isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
                },
                {
                    title: "Lookup types",
                    subtitle: "Manage lookup types",
                    href: "/herenow/lookuptype",
                    isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
                },
                {
                    title: "Lookup groups",
                    subtitle: "Manage lookup groups",
                    href: "/herenow/lookupgroup",
                    isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
                },
                {
                    title: "Settings",
                    subtitle: "Manage settings",
                    href: "/herenow/settings",
                    isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
                },
                {
                    title: "Banner",
                    subtitle: "Manage banners",
                    href: "/herenow/banner",
                    isDisplayed: ClaimHelper.hasRole([roles.SystemAdmin]),
                },
            ],
        },
    ];

    return result;
}